import {
	ATTRIBUTES,
	CHECKED,
	VENDORS,
	type VendorGroup,
	vsTitle,
} from "@marketing/compare/vendors";
import { InShort, Prose, RelatedPages } from "@marketing/components/answer";
import {
	marketingHead,
	PageIntro,
	Section,
} from "@marketing/components/blocks";
import { Closing } from "@marketing/home/Closing";
import { Faq } from "@marketing/home/Faq";
import { createFileRoute } from "@tanstack/react-router";

const GROUPS: {
	group: VendorGroup;
	id: string;
	title: string;
	lead: string;
}[] = [
	{
		group: "gulf",
		id: "gulf-title",
		title: "Gulf and Arabic-first vendors",
		lead: "Platforms that AI assistants name for Arabic chat, WhatsApp and Gulf retail.",
	},
	{
		group: "global",
		id: "global-title",
		title: "Global AI shopping assistants and search",
		lead: "The vendors that hold the global AI shopping assistant and grocery search category.",
	},
	{
		group: "other",
		id: "other-title",
		title: "Other routes",
		lead: "Building your own, and a shopper's own personal assistant.",
	},
];

const FAQ = [
	{
		q: "Where do the facts on these pages come from?",
		a: "From each vendor's own website, documentation, pricing page and llms.txt, and from launch coverage where a vendor has little documentation. Every row links its source, and every page shows the date we checked it.",
	},
	{
		q: 'What does "not stated publicly" mean?',
		a: "That we could not find it on the vendor's public pages on the date we checked. It does not mean the vendor lacks it. Ask them, and tell us if we got something wrong.",
	},
	{
		q: "Is Hephon Commerce always the better choice?",
		a: "No. Every page has a section on where the other vendor is stronger, and several are a better fit for Salla stores, Shopify brands, support-first teams or large site-search programmes.",
	},
	{
		q: "Why should I trust a comparison written by a vendor?",
		a: "You should check it. We make Hephon Commerce, so we cite a source for every row and give you five tests to run on your own catalog with any vendor, including us.",
	},
];

export const Route = createFileRoute("/_marketing/compare/")({
	head: () =>
		marketingHead({
			title: "Compare AI Shopping Assistants | Hephon Commerce",
			description:
				"Compare AI shopping assistants for grocery retailers: Hephon Commerce against 11 vendors and a custom build, with a dated source for every feature claimed.",
			path: "/compare",
			breadcrumb: ["Compare"],
			faq: FAQ,
			updated: CHECKED,
		}),
	component: CompareHub,
});

function CompareHub() {
	return (
		<>
			<PageIntro
				kicker="Compare"
				title="Hephon Commerce compared with other AI shopping assistants"
				lead="Side by side with the vendors grocery retailers ask us about, with a source for every row and the places where they are stronger."
			/>

			<InShort updated={CHECKED}>
				<p>
					These pages compare Hephon Commerce, an AI shopping
					assistant for supermarkets and grocery retailers in Saudi
					Arabia and the UAE, with 11 other vendors and with building
					your own on Azure OpenAI or OpenAI.
				</p>
				<p>
					Each one covers {ATTRIBUTES.length} points, from Arabic
					dialects and WhatsApp ordering to branch prices, pricing and
					data, with the vendor's own page as the source. Each says
					who should choose the other vendor.
				</p>
			</InShort>

			{GROUPS.map(({ group, id, title, lead }, i) => (
				<Section
					key={group}
					id={id}
					title={title}
					lead={lead}
					tone={i % 2 === 1 ? "mist" : "white"}
				>
					<ul className="grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
						{VENDORS.filter((vendor) => vendor.group === group).map(
							(vendor) => (
								<li
									key={vendor.slug}
									className="border-(--mk-line) border-t pt-5"
								>
									<h3 className="font-semibold text-[19px] tracking-[-0.02em]">
										<a
											href={`/compare/${vendor.slug}`}
											className="hover:underline hover:underline-offset-4"
										>
											{vsTitle(vendor)}
										</a>
									</h3>
									<p className="mt-2 text-(--mk-muted) text-[16px] leading-relaxed">
										{vendor.summary}
									</p>
									<p className="mt-2 text-(--mk-ink-2) text-[15px]">
										Best for: {vendor.bestFor}
									</p>
								</li>
							),
						)}
					</ul>
				</Section>
			))}

			<Section id="method-title" title="How we compare">
				<Prose className="mx-0">
					<p>
						We read each vendor's own pages: product and solution
						pages, documentation, pricing and their llms.txt file.
						For Superchat and for Grandiose's GrandChef, which have
						little public documentation, we used launch coverage.
						Hephon Commerce's column uses only facts we publish on
						this site.
					</p>
					<p>
						Where a vendor's pages do not say, we write "not stated
						publicly" rather than guess. Every page lists where the
						other vendor is stronger. If something is out of date,
						write to us at hello@hephon.ai and we will correct it.
					</p>
					<p>
						For a verdict by retailer type, read{" "}
						<a href="/best-ai-shopping-assistants">
							the best AI shopping assistants for grocery
							retailers
						</a>
						.
					</p>
				</Prose>
			</Section>

			<Faq items={FAQ} title="About these comparisons." />
			<RelatedPages
				links={[
					{
						href: "/best-ai-shopping-assistants",
						label: "Best AI shopping assistants for grocery",
					},
					{
						href: "/how-it-works",
						label: "How Hephon Commerce works",
					},
					{ href: "/integrations", label: "Integrations" },
					{ href: "/security", label: "Security and data" },
					{ href: "/demo", label: "Book a demo" },
				]}
			/>
			<Closing />
		</>
	);
}
